// canonicalVoicelines — kid-side playback of the per-(canonical, pet-
// voice) audio rows generated by `generate-canonical-voicelines`.
//
// Flow: parent flips kid_visible on a kitchen item → MCMItemCard
// fires the edge fn → ElevenLabs renders one mp3 per pet voice →
// caches in canonical_voicelines (canonical_id, voice_id, audio_url).
// Now the kid taps the Eggwich tile in their Foods tab → we look up
// their current pet's voice_id → fetch the matching audio_url →
// play. Cat pet hears the cat voice; alien hears alien.
//
// Module-scope caches so the second tap on the same tile is instant
// (no DB roundtrip, no re-fetch of bytes — HTMLAudio.currentTime=0
// rewinds the same buffer).
//
// Mute respects the same localStorage flag as kidSfx / kidVoice so
// one toggle silences everything kid-audio-shaped.

import { supabase } from "./supabase";

// pet_slug → voice_id
const petVoiceCache = new Map();
// (canonical_id, voice_id) → audio_url
const voicelineUrlCache = new Map();
// audio_url → HTMLAudio
const audioElCache = new Map();

function audioEnabled() {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem("mise.kidSfx.enabled") !== "false";
  } catch {
    return true;
  }
}

function voicelineKey(targetKind, canonicalId, voiceId) {
  return `${targetKind}::${canonicalId}::${voiceId}`;
}

async function voiceIdForPet(petSlug) {
  if (!petSlug) return null;
  if (petVoiceCache.has(petSlug)) return petVoiceCache.get(petSlug);
  const { data, error } = await supabase
    .from("pet_catalog")
    .select("voice_id")
    .eq("slug", petSlug)
    .maybeSingle();
  if (error) {
    console.warn("[canonicalVoicelines] pet voice lookup:", error.message);
    return null;
  }
  const voiceId = data?.voice_id || null;
  petVoiceCache.set(petSlug, voiceId);
  return voiceId;
}

async function urlFor(targetKind, canonicalId, voiceId) {
  const k = voicelineKey(targetKind, canonicalId, voiceId);
  if (voicelineUrlCache.has(k)) return voicelineUrlCache.get(k);
  const { data, error } = await supabase
    .from("canonical_voicelines")
    .select("audio_url")
    .eq("target_kind", targetKind)
    .eq("canonical_id", canonicalId)
    .eq("voice_id", voiceId)
    .maybeSingle();
  if (error) {
    console.warn("[canonicalVoicelines] url lookup:", error.message);
    return null;
  }
  const url = data?.audio_url || null;
  voicelineUrlCache.set(k, url);
  return url;
}

// Look up the voiceline URL for a (target, canonical/recipe, pet)
// combo. Used by the kidVoice sequencer to splice the food name
// into a narrate sequence like ["your", canonicalUrl, "is-in-the-oven"].
export async function getCanonicalVoicelineUrl(canonicalId, petSlug, targetKind = "canonical") {
  if (!canonicalId || !petSlug) return null;
  const voiceId = await voiceIdForPet(petSlug);
  if (!voiceId) return null;
  return urlFor(targetKind, canonicalId, voiceId);
}

export async function playRecipeVoiceline(recipeSlug, petSlug) {
  return playCanonicalVoiceline(recipeSlug, petSlug, "recipe");
}

export async function playCanonicalVoiceline(canonicalId, petSlug, targetKind = "canonical") {
  if (!audioEnabled() || !canonicalId) return false;
  const voiceId = await voiceIdForPet(petSlug);
  if (!voiceId) return false;
  const url = await urlFor(targetKind, canonicalId, voiceId);
  if (!url) return false;
  let el = audioElCache.get(url);
  if (!el) {
    el = new Audio(url);
    el.preload = "auto";
    audioElCache.set(url, el);
  }
  try {
    el.currentTime = 0;
    const p = el.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
    return true;
  } catch {
    return false;
  }
}

// Clear caches — call when the kid switches pet or admin re-bakes
// a voiceline. Cheap; only resets in-memory maps.
export function resetCanonicalVoicelineCaches() {
  petVoiceCache.clear();
  voicelineUrlCache.clear();
  audioElCache.clear();
}
